import { useEffect, useState } from "react";

/**
 * Throwaway-canvas WebGL feature-detection probe (ZP-91). Never attaches
 * anything to the DOM — creates an in-memory canvas, asks it for a WebGL2
 * or WebGL context, and discards it. `getContext` can itself throw on some
 * locked-down browsers/extensions rather than just returning null, so this
 * is wrapped defensively.
 */
function detectWebGL(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    return !!gl;
  } catch {
    return false;
  }
}

/**
 * Heuristic-only "is this probably a low-power device" check (ZP-91's
 * "or is low-powered" fallback branch). `hardwareConcurrency` and
 * `deviceMemory` are both optional and inconsistently implemented across
 * browsers — this isn't precise, but a conservative signal is enough to
 * keep the 3D scene off battery/CPU-constrained hardware rather than
 * chasing perfect detection.
 */
function isLikelyLowPowerDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const cores = navigator.hardwareConcurrency;
  if (typeof cores === "number" && cores > 0 && cores <= 2) return true;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (typeof memory === "number" && memory > 0 && memory < 4) return true;
  return false;
}

/**
 * Combined WebGL-availability + low-power-device check, resolved once on
 * mount (checked once, per ZP-91's tech notes — this doesn't change
 * mid-session, so there's nothing to re-poll). Starts `false` so the very
 * first render never optimistically assumes 3D support; callers should
 * gate mounting the 3D scene on this becoming `true`, and treat `false` as
 * "stay on the static fallback" — which for this hero is simply not
 * mounting the 3D layer, since the poster underneath is already the
 * fallback.
 */
export default function useWebGLSupport(): boolean {
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported(detectWebGL() && !isLikelyLowPowerDevice());
  }, []);

  return supported;
}
